import React, { useCallback, useEffect, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import packageJson from '../package.json';
import AppRoute from './AppRoute';
import environment from './environments/environment';
import { FeatureConfig } from './models/featureFlag';
import { FeatureContext } from './FeatureContext';
import { FeatureService } from './services';
import { AppHidden } from './components/core/AppHidden';
import { TimeOutBar } from './components/core/TimeOutBar';

declare global {
  interface Window {
    enableFeature: (featureName: string) => void;
    disableFeature: (featureName: string) => void;
    dataLayer: Record<string, any>[];
    rateSwitchAppVersion: string;
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Version = styled(Typography)`
  position: fixed;
  bottom: 0;
  right: 0;
`;

function App() {
  const [features, setFeatures] = useState<FeatureConfig>({});

  const toggleFeatures = useCallback(
    (featureName: string, toggle: boolean) => {
      if (!Object.keys(features).includes(featureName)) {
        // eslint-disable-next-line no-console
        console.error(`Provided value "${featureName}" is not a valid feature flag`);
        return;
      }
      const featureObj = {
        ...features,
        [featureName]: toggle,
      };
      setFeatures(featureObj);
    },
    [features],
  );

  useEffect(() => {
    const getFeatures = async () => {
      const res = await FeatureService.getFeatures();
      setFeatures(res);
    };

    getFeatures();
  }, []);

  useEffect(() => {
    window.rateSwitchAppVersion = packageJson.version;
    if (!environment.featureToggle) return;

    window.enableFeature = (featureName: string) => toggleFeatures(featureName, true);
    window.disableFeature = (featureName: string) => toggleFeatures(featureName, false);
  }, [toggleFeatures]);

  return (
    <Box component="main" sx={{ display: 'flex' }}>
      <FeatureContext.Provider value={features}>
        <Box sx={{ flexGrow: 1 }}>
          <AppHidden />
          <TimeOutBar />
          <AppRoute />
        </Box>
      </FeatureContext.Provider>
      {features.SHOW_APP_VERSION && (
      <Version variant="caption">
        Version:
        {' '}
        {window.rateSwitchAppVersion}
      </Version>
      )}
    </Box>
  );
}

export default App;
